import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, ArrowLeft, UserCheck, Wrench, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/site/Logo";
import { NotificationBell } from "../components/site/NotificationBell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (s) => { try { return new Date(s).toLocaleDateString(); } catch { return s; } };

const REPAIR_BADGE = {
  Submitted: "bg-[#EAF7FC] text-[#133955]", Assigned: "bg-[#EAF7FC] text-[#133955]",
  "Walkthrough Requested": "bg-[#FFE8B0] text-[#7a5b00]", "Walkthrough Scheduled": "bg-[#FFE8B0] text-[#7a5b00]",
  Quoted: "bg-[#FFE8B0] text-[#7a5b00]", "Quote Accepted": "bg-[#D8F3E3] text-[#0f6b3d]",
  "Quote Declined": "bg-[#FBD5D5] text-[#9b1c1c]", Completed: "bg-[#D8F3E3] text-[#0f6b3d]", Cancelled: "bg-[#FBD5D5] text-[#9b1c1c]",
};

export default function AdminAgentProfile() {
  const { aid } = useParams();
  const { token, role, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const saveEdit = async (form) => {
    await axios.patch(`${API}/admin/agents/${aid}`, form, authHeader);
    toast.success("Agent profile updated.");
    setEditOpen(false);
    await load();
  };

  useEffect(() => {
    if (!authLoading && !token) navigate("/admin/login");
    if (!authLoading && token && role && role !== "admin") navigate(role === "agent" ? "/agent" : "/portal");
  }, [authLoading, token, role, navigate]);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/agents/${aid}/profile`, { headers: { Authorization: `Bearer ${token}` } });
    setData(data);
  }, [aid, token]);

  useEffect(() => {
    if (!token) return;
    (async () => { try { await load(); } catch (e) { if (e?.response?.status === 401) { logout(); navigate("/admin/login"); } } finally { setLoading(false); } })();
  }, [token, load, logout, navigate]);

  if (authLoading || loading) return <div className="min-h-screen grid place-items-center bg-[#EAF7FC]"><Loader2 className="animate-spin text-[#133955]" size={32} /></div>;
  if (!data) return (
    <div className="min-h-screen grid place-items-center bg-[#EAF7FC]" data-testid="agent-profile-missing">
      <div className="text-center"><p className="text-[#173042] font-semibold">Agent not found.</p>
        <button onClick={() => navigate("/admin/agents")} className="mt-4 rounded-full bg-[#133955] text-white px-6 py-2.5 text-sm font-bold">Back to Agents</button></div>
    </div>
  );

  const a = data.agent;

  return (
    <div className="min-h-screen bg-[#EAF7FC]" data-testid="admin-agent-profile">
      <header className="bg-white border-b border-[rgba(19,57,85,0.08)] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <button data-testid="nav-back-agents" onClick={() => navigate("/admin/agents")} className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold text-[#133955] hover:bg-[#EAF7FC]"><ArrowLeft size={16} /> Agents</button>
            <NotificationBell />
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-2 rounded-full border-2 border-[#133955] text-[#133955] px-5 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white"><LogOut size={16} /> Sign Out</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        {/* Profile card */}
        <div className="rounded-2xl bg-white border border-[rgba(19,57,85,0.08)] p-6 mb-6" data-testid="agent-profile-card">
          <div className="flex items-start gap-4 flex-wrap">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-[#133955] text-white shrink-0"><UserCheck size={26} /></span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-bold text-[#54BCE6]">{a.agent_ref}</span>
                <h1 className="font-display text-3xl font-extrabold text-[#133955] tracking-tight">{a.name}</h1>
                <span className="rounded-full px-2.5 py-0.5 text-xs font-bold bg-[#EAF7FC] text-[#133955] capitalize" data-testid="profile-account-badge">Account: {a.account_status}</span>
                <button data-testid="edit-agent-btn" onClick={() => setEditOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#133955] text-[#133955] px-4 py-1.5 text-xs font-bold hover:bg-[#133955] hover:text-white transition-colors">
                  <Pencil size={13} /> Edit Profile
                </button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1.5 mt-4 text-sm">
                <div><span className="text-[#173042]/50">Brokerage:</span> <strong className="text-[#173042]">{a.brokerage || "—"}</strong></div>
                <div className="break-all"><span className="text-[#173042]/50">Email:</span> {a.email}</div>
                <div><span className="text-[#173042]/50">Phone:</span> {a.phone || "—"}</div>
                <div><span className="text-[#173042]/50">Member since:</span> {fmt(a.created_at)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Repair requests */}
        <section>
          <h2 className="font-display text-xl font-extrabold text-[#133955] mb-3 flex items-center gap-2"><Wrench size={18} className="text-[#54BCE6]" /> Repair Requests <span className="text-sm font-semibold text-[#173042]/50">({data.requests.length})</span></h2>
          {data.requests.length === 0 ? (
            <p className="rounded-xl bg-white border border-[rgba(19,57,85,0.08)] p-5 text-sm text-[#173042]/60" data-testid="no-agent-requests">No repair requests submitted yet.</p>
          ) : (
            <div className="space-y-2" data-testid="profile-agent-requests">
              {data.requests.map((r) => (
                <button key={r.id} data-testid={`profile-agent-request-${r.id}`} onClick={() => navigate(`/admin/repair-requests?repair=${r.id}`)}
                  className="w-full text-left rounded-xl bg-white border border-[rgba(19,57,85,0.08)] px-4 py-3 flex items-center gap-3 flex-wrap hover:border-[#54BCE6] transition-colors">
                  <span className="font-mono text-xs font-bold text-[#54BCE6]">{r.request_ref}</span>
                  <span className="font-semibold text-[#133955]">{r.property_address}</span>
                  {r.target_close_date && <span className="text-sm text-[#173042]/60">Close: {r.target_close_date}</span>}
                  <span className="ml-auto flex items-center gap-2">
                    {(() => { const al = r.assigned_contractors?.length ? r.assigned_contractors : (r.assigned_contractor_ref ? [{ contractor_ref: r.assigned_contractor_ref, contractor_name: r.assigned_contractor_name }] : []); return al.length
                      ? <span className="text-xs font-semibold text-[#0f6b3d]">{al.map((e) => `${e.contractor_ref} · ${e.contractor_name}${e.role_label ? ` (${e.role_label})` : ""}`).join(", ")}</span>
                      : <span className="text-xs text-[#173042]/50">Unassigned</span>; })()}
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${REPAIR_BADGE[r.status] || REPAIR_BADGE.Submitted}`} data-testid={`profile-agent-request-status-${r.id}`}>{r.status}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      </main>

      <EditAgentDialog open={editOpen} onClose={() => setEditOpen(false)} agent={a} onSave={saveEdit} />
    </div>
  );
}

// Admin: edit agent profile details (email changes sync their login account)
function EditAgentDialog({ open, onClose, agent, onSave }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && agent) {
      setForm({ name: agent.name || "", brokerage: agent.brokerage || "", email: agent.email || "", phone: agent.phone || "" });
    }
  }, [open, agent]);

  if (!open || !form) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required."); return; }
    setBusy(true);
    try { await onSave(form); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save changes."); }
    finally { setBusy(false); }
  };

  const fld = (key, label, opts = {}) => (
    <div className={opts.full ? "sm:col-span-2" : ""}>
      <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">{label}</label>
      <input data-testid={`edit-a-${key}`} type={opts.type || "text"} value={form[key]} onChange={set(key)} className="hpc-input bg-white" placeholder={opts.placeholder || ""} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white" data-testid="edit-agent-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#133955] text-2xl">Edit Agent</DialogTitle>
          <DialogDescription className="text-[#173042]/70">
            Changes save immediately. Editing the email changes their sign-in email too.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 py-1">
          {fld("name", "Full Name *")}
          {fld("brokerage", "Brokerage")}
          {fld("email", "Email (sign-in) *", { full: true, type: "email" })}
          {fld("phone", "Phone", { full: true })}
        </div>
        <DialogFooter className="gap-3">
          <button data-testid="edit-a-cancel" onClick={onClose} className="rounded-full border-2 border-[#133955] text-[#133955] px-6 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white transition-colors">Cancel</button>
          <button data-testid="edit-a-save" onClick={submit} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-6 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-50">
            {busy && <Loader2 size={16} className="animate-spin" />} {busy ? "Saving…" : "Save Changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
